/**
 * Extended performance data hook for the Performance Page.
 * Fetches performance data and derives drawdown, monthly returns, and stats.
 */

import { useMemo, useRef } from 'react';
import useSWR from 'swr';
import { fetchPerformance } from '@/services/api';
import {
  calculatePerformanceStats,
  calculateDrawdown,
  calculateMonthlyReturns,
} from '@/utils/performanceCalculations';
import type {
  ExtendedTimeRange,
  PerformanceDataPoint,
  BenchmarkDataPoint,
  PerformanceStats,
  DrawdownDataPoint,
  MonthlyReturn,
} from '@/types/performance';

interface UsePerformanceDataOptions {
  timeFrame: ExtendedTimeRange;
  includeBenchmark?: boolean;
  benchmarkSymbol?: string;
}

interface UsePerformanceDataReturn {
  data: PerformanceDataPoint[];
  benchmarkData: BenchmarkDataPoint[] | null;
  stats: PerformanceStats;
  drawdownData: DrawdownDataPoint[];
  monthlyReturns: MonthlyReturn[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  lastUpdated: string | null;
}

const emptyStats: PerformanceStats = {
  totalReturn: 0,
  totalReturnPercent: 0,
  startValue: 0,
  endValue: 0,
  maxValue: 0,
  minValue: 0,
  avgDailyPnl: 0,
  maxDrawdown: 0,
  maxDrawdownStartDate: '',
  maxDrawdownEndDate: '',
  alpha: null,
};

/**
 * Convert ExtendedTimeRange to number of days.
 */
function timeFrameToDays(timeFrame: ExtendedTimeRange): number {
  switch (timeFrame) {
    case '1w':
      return 7;
    case '1m':
      return 30;
    case '3m':
      return 90;
    case '6m':
      return 180;
    case 'ytd': {
      const now = new Date();
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const diffMs = now.getTime() - startOfYear.getTime();
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    }
    case '1y':
      return 365;
    case 'all':
      return 1825;
    default:
      return 30;
  }
}

/**
 * Transform API response data into PerformanceDataPoint[] by adding cumulativeReturn.
 */
function transformData(
  rawData: Array<{ date: string; value: number; pnl: number }>
): PerformanceDataPoint[] {
  if (rawData.length === 0) return [];

  const startValue = rawData[0]!.value;

  return rawData.map((point) => ({
    date: point.date,
    value: point.value,
    pnl: point.pnl,
    cumulativeReturn:
      startValue !== 0 ? ((point.value - startValue) / startValue) * 100 : 0,
  }));
}

/**
 * Fetch and derive comprehensive performance data for an account.
 *
 * @param accountId - The account ID to fetch performance for (null disables fetching)
 * @param options - Configuration including timeFrame and benchmark settings
 * @returns Performance data, derived calculations, loading state, and controls
 */
export function usePerformanceData(
  accountId: string | null,
  options: UsePerformanceDataOptions
): UsePerformanceDataReturn {
  const { timeFrame } = options;
  const days = timeFrameToDays(timeFrame);
  const lastUpdatedRef = useRef<string | null>(null);

  const { data: rawData, error, isLoading, mutate } = useSWR(
    accountId ? ['performanceData', accountId, days] : null,
    () => (accountId ? fetchPerformance(accountId, days) : []),
    {
      refreshInterval: 300000, // 5 minutes
      revalidateOnFocus: false,
      dedupingInterval: 60000,
      onSuccess: () => {
        lastUpdatedRef.current = new Date().toISOString();
      },
    }
  );

  // Transform raw API data into PerformanceDataPoint[]
  const data = useMemo(() => transformData(rawData ?? []), [rawData]);

  // Derive performance stats
  const stats = useMemo(() => {
    if (data.length === 0) return emptyStats;
    return calculatePerformanceStats(data);
  }, [data]);

  // Derive drawdown data
  const drawdownData = useMemo(() => {
    if (data.length === 0) return [];
    return calculateDrawdown(data);
  }, [data]);

  // Derive monthly returns
  const monthlyReturns = useMemo(() => {
    if (data.length === 0) return [];
    return calculateMonthlyReturns(data);
  }, [data]);

  return {
    data,
    benchmarkData: null,
    stats,
    drawdownData,
    monthlyReturns,
    isLoading,
    error: error?.message ?? null,
    refresh: () => { mutate(); },
    lastUpdated: lastUpdatedRef.current,
  };
}
